import { useCallback, useEffect, useState } from "react";
import { AdminScheduledTasksDocument } from "#app/lib/data";
import type {
  CronSchedulerMode,
  ScheduledTaskRow,
  ScheduledTaskRunRow,
} from "#app/lib/data";
import { useAdminFetch } from "./admin-shared";

type TasksPayload = {
  tasks: ScheduledTaskRow[];
  runs: ScheduledTaskRunRow[];
  mode: CronSchedulerMode;
  registered: boolean;
};

const NO_TASKS: ScheduledTaskRow[] = [];
const NO_RUNS: ScheduledTaskRunRow[] = [];

/**
 * Scheduled-task observability: the cron catalog with next fire times plus
 * recent run history, read server-side from the BullMQ queue's retained jobs.
 * Strictly read-only — the sweeps are single-producer and have no on-demand
 * trigger by design, so there is no "run now" action to offer.
 *
 * Rows travel to the screen exactly as the query returns them; the counts below
 * are the one rollup, so the nav dot and the screen's tiles can never disagree
 * about what is wrong.
 */
export function useAdminTasks() {
  const { coreGql } = useAdminFetch();
  const [data, setData] = useState<TasksPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastChecked, setLastChecked] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const result = await coreGql(AdminScheduledTasksDocument, {});
      setData(result.adminScheduledTasks);
      setError(null);
      setLastChecked(new Date().toLocaleTimeString());
    } catch (err: unknown) {
      // Without this the query's rejection is unhandled and `data` stays null,
      // which renders as "0 tasks, 0 runs, 0 failures" — a total failure to
      // load, displayed as a healthy idle scheduler.
      setError(err instanceof Error ? err.message : "Failed to load scheduled tasks");
    } finally {
      setLoading(false);
    }
  }, [coreGql]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const tasks = data?.tasks ?? NO_TASKS;
  const runs = data?.runs ?? NO_RUNS;

  return {
    tasks,
    runs,
    // Null until a response lands, and null again for a load that failed —
    // consumers must not read "not disabled" off it as "running".
    mode: data?.mode ?? null,
    registered: data?.registered ?? false,
    loading,
    error,
    lastChecked,
    refresh,
    failedCount: runs.filter((r) => r.status === "failed").length,
    overdueCount: tasks.filter((t) => t.overdue).length,
  };
}
